import AirportScreen from '@/components/airline-admin/airport-note/detail';
import Spin from '@/components/common/spin';
import ATCLayout from '@/layouts/atc-layout';
import Page404 from '@/pages/status/page-404';
import {type AppRouteObject} from '@/router/interface';
import {Suspense, lazy} from 'react';
import {Navigate} from 'react-router-dom';

// Lazy load the routes to improve performance
const HomeScreen = lazy(() => import('@/pages/home'));
const SignInScreen = lazy(() => import('@/pages/login/sign-in'));
const DashboardScreen = lazy(() => import('@/pages/dashboard/index'));
const CiCalculatorScreen = lazy(() => import('@/pages/cicalculator/index'));
const TestDelayScreen = lazy(() => import('@/pages/test-delay'));
const DebugScreen = lazy(() => import('@/pages/debug'));
const TestApiScreen = lazy(() => import('@/pages/test-api'));
const MockFlightInsightScreen = lazy(
  () => import('@/pages/mock-flight-insight')
);
const PresentationScreen = lazy(() => import('@/pages/presentation'));
const OnboardingAdminScreen = lazy(() => import('@/pages/onboarding-admin'));
const AgentExecutionListScreen = lazy(
  () => import('@/pages/agent-execution/index')
);
const AgentExecutionDetailScreen = lazy(
  () => import('@/pages/agent-execution/detail')
);
const AirlineAdminScreen = lazy(() => import('@/pages/airline-admin/index'));
const AirlineAdminHomeScreen = lazy(() => import('@/pages/airline-admin/home'));
const AirlineAdminVersionControlScreen = lazy(
  () => import('@/pages/airline-admin/version-control')
);
const AirlineAdminVersionControlReviewScreen = lazy(
  () => import('@/pages/airline-admin/version-control-review')
);
const AirlineAdminAirportInfoScreen = lazy(
  () => import('@/pages/airline-admin/airport-information')
);
const AirlineAdminUsersScreen = lazy(
  () => import('@/pages/airline-admin/users')
);
const AirlineAdminSystemDocsScreen = lazy(
  () => import('@/pages/airline-admin/system-documentation')
);
const AirlineAdminChatSupportScreen = lazy(
  () => import('@/pages/airline-admin/chat-support')
);
const NetworkScreen = lazy(() => import('@/pages/network'));
const LiveEmissionScreen = lazy(() => import('@/pages/atc/live-emission'));
const EmissionPerformanceScreen = lazy(
  () => import('@/pages/atc/emission-performance')
);
const FlightPlanLearningScreen = lazy(
  () => import('@/pages/flight-plan-learning')
);

const mainHomeScreen = () => {
  if (import.meta.env.VITE_APP_TYPE === 'ONBOARDING_ADMIN') {
    return <Navigate to="/dashboard/onboarding-admin" />;
  }
  if (import.meta.env.VITE_APP_TYPE === 'AIRLINE_ADMIN') {
    return <Navigate to="/dashboard/airline-admin" />;
  }
  if (import.meta.env.VITE_APP_TYPE === 'ATC') {
    return <Navigate to="/atc" />;
  }
  return <HomeScreen />;
};

const routes: AppRouteObject[] = [
  {
    path: '/',
    children: [
      {
        index: true,
        element: <SignInScreen />
      },
      {
        path: 'login',
        element: <SignInScreen />
      }
    ]
  },
  {
    path: 'atc',
    isProtected: true,
    element: <ATCLayout />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<Spin />}>
            <LiveEmissionScreen />
          </Suspense>
        )
      },
      {
        path: 'performance',
        element: (
          <Suspense fallback={<Spin />}>
            <EmissionPerformanceScreen />
          </Suspense>
        )
      }
    ]
  },
  {
    path: 'dashboard',
    isProtected: true,
    children: [
      {
        index: true,
        element: <Suspense fallback={<Spin />}>{mainHomeScreen()}</Suspense>
      },
      {
        path: 'presentation',
        element: (
          <Suspense fallback={<Spin />}>
            <PresentationScreen />
          </Suspense>
        )
      },
      {
        path: 'onboarding-admin',
        element: (
          <Suspense fallback={<Spin />}>
            <OnboardingAdminScreen />
          </Suspense>
        )
      },
      {
        path: 'airline-admin',
        element: (
          <Suspense fallback={<Spin />}>
            <AirlineAdminScreen />
          </Suspense>
        ),
        children: [
          {
            index: true,
            path: '',
            element: (
              <Suspense fallback={<Spin />}>
                <AirlineAdminHomeScreen />
              </Suspense>
            )
          },
          {
            path: 'home',
            element: (
              <Suspense fallback={<Spin />}>
                <AirlineAdminHomeScreen />
              </Suspense>
            )
          },
          {
            path: 'version-control',
            element: (
              <Suspense fallback={<Spin />}>
                <AirlineAdminVersionControlScreen />
              </Suspense>
            )
          },
          {
            path: 'version-control-review/:versionId',
            element: (
              <Suspense fallback={<Spin />}>
                <AirlineAdminVersionControlReviewScreen />
              </Suspense>
            )
          },
          {
            path: 'airport-information',
            element: (
              <Suspense fallback={<Spin />}>
                <AirlineAdminAirportInfoScreen />
              </Suspense>
            )
          },
          {
            path: 'airport-note',
            element: (
              <Suspense fallback={<Spin />}>
                <AirportScreen />
              </Suspense>
            )
          },
          {
            path: 'users',
            element: (
              <Suspense fallback={<Spin />}>
                <AirlineAdminUsersScreen />
              </Suspense>
            )
          },
          {
            path: 'system-documentation',
            element: (
              <Suspense fallback={<Spin />}>
                <AirlineAdminSystemDocsScreen />
              </Suspense>
            )
          },
          {
            path: 'chat-support',
            element: (
              <Suspense fallback={<Spin />}>
                <AirlineAdminChatSupportScreen />
              </Suspense>
            )
          }
        ]
      },
      {
        path: 'manager',
        element: (
          <Suspense fallback={<Spin />}>
            <DashboardScreen />
          </Suspense>
        )
      },
      {
        path: 'network',
        element: (
          <Suspense fallback={<Spin />}>
            <NetworkScreen />
          </Suspense>
        )
      },
      {
        path: 'cicalculator',
        element: (
          <Suspense fallback={<Spin />}>
            <CiCalculatorScreen />
          </Suspense>
        )
      },
      {
        path: 'test-delay',
        element: (
          <Suspense fallback={<Spin />}>
            <TestDelayScreen />
          </Suspense>
        )
      },
      {
        path: 'debug',
        element: (
          <Suspense fallback={<Spin />}>
            <DebugScreen />
          </Suspense>
        )
      },
      {
        path: 'test-api',
        element: (
          <Suspense fallback={<Spin />}>
            <TestApiScreen />
          </Suspense>
        )
      },
      {
        path: 'mock-flight-insight',
        element: (
          <Suspense fallback={<Spin />}>
            <MockFlightInsightScreen />
          </Suspense>
        )
      },
      {
        path: 'flight-plan-learning',
        element: (
          <Suspense fallback={<Spin />}>
            <FlightPlanLearningScreen />
          </Suspense>
        )
      },
      ...(import.meta.env.VITE_APP_TYPE === 'ONBOARDING_ADMIN'
        ? [
            {
              path: 'agent-execution',
              element: (
                <Suspense fallback={<Spin />}>
                  <AgentExecutionListScreen />
                </Suspense>
              )
            },
            {
              path: 'agent-execution/:conversationId',
              element: (
                <Suspense fallback={<Spin />}>
                  <AgentExecutionDetailScreen />
                </Suspense>
              )
            }
          ]
        : [])
    ]
  },
  {
    path: '/*',
    element: <Page404 />
  }
];

export default routes;
